import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store/auth';
import { useLogin } from '@/features/auth/hooks/useLogin';
import { useMFAValidate } from '@/features/auth/hooks/useMFAValidate';
import { useResendMFA } from '@/features/auth/hooks/useResendMFA';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { LoginForm, MFAForm } from '@/features/auth/components/ui/organisms';
import type { LoginFormData } from '@/features/auth/components/ui/organisms';
import type {
  FederatedLoginLocationState,
  LoginLocationState,
} from '@/features/auth/types/auth.types';
import './AuthPage.css';
import type { MFAFormData } from './types';

/**
 * AuthPage Component
 *
 * A page container that orchestrates the authentication flow.
 * Acts as a factory pattern, rendering the appropriate form organism
 * based on the current authentication step (Login, MFA).
 *
 * Federated (SSO) states, per Figma "[PU] Home Unificada":
 * - CA05 ("[Erro 01] Federado tenta login local"): the login stays on this
 *   screen with the "SSO Identificado" alert; the primary action conducts the
 *   user to `/sso` carrying the typed e-mail via router state (never the URL).
 * - CA10 ("[Login] Não federado tenta acessa via SSO"): the corporate flow
 *   returns here preserving the typed e-mail, with the "Conta não vinculada a
 *   SSO" alert above the regular credential form.
 * - CA11: the user authenticated on the company IdP but has no access granted
 *   on the platform; the session bootstrap sends them back here with the
 *   "acesso ainda não liberado" notice, pointing to their company's access
 *   manager — never presented as invalid credentials.
 *
 * @returns The complete authentication page with step management
 */
const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { currentStep, sessionId, handleBackToLogin, setError, error } =
    useAuthStore();

  const returnUrl = searchParams.get('returnUrl');

  // CA10 — o fluxo corporativo devolveu o usuário para cá preservando o
  // e-mail digitado (router state, nunca na URL).
  const loginState = (location.state ?? null) as LoginLocationState | null;
  const [accessDeniedNotice, setAccessDeniedNotice] =
    useState<LoginLocationState | null>(() =>
      loginState?.notice === 'access-not-granted' ? loginState : null
    );

  useEffect(() => {
    if (loginState?.notice !== 'access-not-granted') return;

    setAccessDeniedNotice(loginState);
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: null,
    });
  }, [loginState, location.hash, location.pathname, location.search, navigate]);

  // CA05 — o backend identificou um usuário federado tentando senha: o login
  // permanece nesta tela com o alerta "SSO Identificado" (Figma [Erro 01]).
  const [ssoIdentified, setSsoIdentified] =
    useState<FederatedLoginLocationState | null>(null);

  const ssoState = ssoIdentified
    ? 'identified'
    : loginState?.notice === 'sso-not-available'
      ? 'not-linked'
      : // CA11 — o bootstrap da sessão recusou o acesso por falta de
        // autorização na plataforma e devolveu o usuário para cá.
        accessDeniedNotice?.notice === 'access-not-granted'
        ? 'access-not-granted'
        : null;

  const { mutate: loginMutate, isPending: isLoginPending } = useLogin({
    returnUrl: returnUrl ? decodeURIComponent(returnUrl) : undefined,
    onFederatedIdentified: (state) => setSsoIdentified(state),
  });

  const { mutate: validateMFAMutate } = useMFAValidate({
    onSuccess: () => {
      console.log('MFA validation successful', { returnUrl });
      const redirectTo = returnUrl ? decodeURIComponent(returnUrl) : '/';
      navigate(redirectTo, { replace: true });
    },
    onAccessNotGranted: (email) =>
      navigate('/login', {
        replace: true,
        state: { notice: 'access-not-granted', email },
      }),
  });

  const { mutate: resendMFAMutate } = useResendMFA({
    onError: () => {
      setError(t('Failed to resend code. Please try again.'));
    },
  });

  const handleLoginSubmit = (data: LoginFormData) => {
    loginMutate({ username: data.username, password: data.password });
  };

  // CA05 — conduz ao fluxo corporativo sem redigitar o e-mail: o e-mail (e o
  // registrationId/authenticationUrl já resolvidos) viajam por router state.
  const handleAccessSso = (email: string) => {
    const state: FederatedLoginLocationState = {
      ...(ssoIdentified ?? { reason: 'federated-required' }),
      email,
    };
    navigate('/sso', { state });
  };

  const handleMFASubmit = async (data: MFAFormData) => {
    if (data.code.length !== 6) {
      setError('Invalid MFA code');
      return;
    }

    if (!sessionId) {
      setError('Session expired. Please login again.');
      handleBackToLogin();
      return;
    }

    validateMFAMutate({ sessionId, mfatoken: data.code });
  };

  const handleResendCode = () => {
    if (!sessionId) {
      handleBackToLogin();
      return;
    }

    resendMFAMutate({ sessionId });
  };

  const renderContent = () => {
    switch (currentStep) {
      case 'mfa':
        return (
          <MFAForm
            onBackToLogin={handleBackToLogin}
            onSubmit={handleMFASubmit}
            onResendCode={handleResendCode}
            onClearError={() => setError(null)}
            error={error}
          />
        );

      case 'login':
      default:
        return (
          <LoginForm
            onSubmit={handleLoginSubmit}
            error={error}
            loading={isLoginPending}
            ssoState={ssoState}
            onAccessSso={handleAccessSso}
            onBackToLogin={() => setSsoIdentified(null)}
            defaultEmail={
              ssoIdentified?.email ??
              accessDeniedNotice?.email ??
              loginState?.email ??
              ''
            }
          />
        );
    }
  };

  return (
    <div className="auth-page-container">
      <div className="auth-container" style={{ position: 'relative' }}>
        {renderContent()}
      </div>
    </div>
  );
};

export default AuthPage;
